import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LogOut } from "lucide-react";
import client, { formatApiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import AdminLayout from "./AdminLayout";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";

const roleLabel = (r) => ({ super_admin: "Super Admin", content_admin: "Content Admin", enquiry_admin: "Enquiry Admin", viewer: "Viewer" }[r] || r);

export default function Profile() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [me, setMe] = useState({});
  const [pw, setPw] = useState({ currentPassword: "", newPassword: "", confirm: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => { client.get("/auth/me").then(({ data }) => setMe(data)).catch(() => {}); }, []);

  const change = async (e) => {
    e.preventDefault();
    if (pw.newPassword.length < 8) { toast.error("New password must be at least 8 characters."); return; }
    if (pw.newPassword !== pw.confirm) { toast.error("New password and confirmation do not match."); return; }
    setBusy(true);
    try {
      await client.post("/auth/change-password", { currentPassword: pw.currentPassword, newPassword: pw.newPassword });
      toast.success("Password changed successfully.");
      setPw({ currentPassword: "", newPassword: "", confirm: "" });
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <AdminLayout title="My Profile">
      <div className="grid gap-8 lg:grid-cols-2">
        <div className="rounded-md border border-gold/20 bg-white p-7" data-testid="profile-info">
          <h2 className="mb-4 font-serif text-xl text-maroon">Account</h2>
          <dl className="space-y-3 text-sm">
            <div className="flex justify-between border-b border-gold/10 pb-2"><dt className="text-ink-muted">Name</dt><dd className="text-maroon">{me.name || "—"}</dd></div>
            <div className="flex justify-between border-b border-gold/10 pb-2"><dt className="text-ink-muted">Email</dt><dd className="text-maroon">{me.email}</dd></div>
            <div className="flex justify-between border-b border-gold/10 pb-2"><dt className="text-ink-muted">Role</dt><dd className="text-maroon">{roleLabel(me.role)}</dd></div>
            <div className="flex justify-between border-b border-gold/10 pb-2"><dt className="text-ink-muted">Status</dt><dd className="text-maroon capitalize">{me.status}</dd></div>
            <div className="flex justify-between"><dt className="text-ink-muted">Last Login</dt><dd className="text-maroon">{me.lastLogin ? me.lastLogin.slice(0, 19).replace("T", " ") : "—"}</dd></div>
          </dl>
          <button onClick={() => { logout(); navigate("/admin/login"); }} className="mt-6 inline-flex items-center gap-2 border border-gold/40 px-4 py-2.5 text-xs uppercase tracking-wide text-maroon hover:bg-ivory" data-testid="profile-logout"><LogOut size={15} /> Logout</button>
        </div>

        <form onSubmit={change} className="rounded-md border border-gold/20 bg-white p-7" data-testid="change-password-form">
          <h2 className="mb-4 font-serif text-xl text-maroon">Change Password</h2>
          <div className="space-y-4">
            <div><Label>Current Password</Label><Input className="mt-1.5" type="password" required value={pw.currentPassword} onChange={(e) => setPw({ ...pw, currentPassword: e.target.value })} data-testid="pw-current" /></div>
            <div><Label>New Password</Label><Input className="mt-1.5" type="password" required value={pw.newPassword} onChange={(e) => setPw({ ...pw, newPassword: e.target.value })} data-testid="pw-new" /><p className="mt-1 text-xs text-ink-muted">Minimum 8 characters.</p></div>
            <div><Label>Confirm New Password</Label><Input className="mt-1.5" type="password" required value={pw.confirm} onChange={(e) => setPw({ ...pw, confirm: e.target.value })} data-testid="pw-confirm" /></div>
          </div>
          <button type="submit" disabled={busy} data-testid="pw-submit" className="mt-6 bg-maroon px-6 py-3 text-xs uppercase tracking-wide text-ivory hover:bg-maroon-dark disabled:opacity-60">{busy ? "Saving..." : "Change Password"}</button>
        </form>
      </div>
    </AdminLayout>
  );
}
